import { buildOverlayClusterIndexes, flooredOverlayZoom, renderOverlayClusters } from "./overlay-clustering.js";
import { HEX_OVERLAY_CATEGORIES } from "./overlay-config.js";
import { filterReportsByTime } from "./time-series.js";

// This mirrors the H3 renderer's source-update guard. Time state controls
// index rebuilding; floored map zoom only changes the queried Supercluster view.
export function createHexOverlayRenderer({
  reportsByCategory,
  setFeatureCollections,
  removeActivePopup = () => {},
  buildIndexes = buildOverlayClusterIndexes,
  renderClusters = renderOverlayClusters,
}) {
  assertReportsByCategory(reportsByCategory);
  if (typeof setFeatureCollections !== "function") throw new TypeError("Expected an overlay source-update callback.");
  let displayedKey = null;
  let indexes = null;
  let timeKey = null;

  return {
    update({ zoom, timeState = null }) {
      const nextTimeKey = timeStateKey(timeState);
      if (nextTimeKey !== timeKey) {
        indexes = buildIndexes(filteredOverlayReports(reportsByCategory, timeState));
        timeKey = nextTimeKey;
      }
      const queryZoom = flooredOverlayZoom(zoom);
      const nextDisplayedKey = `${timeKey}:${queryZoom}`;
      if (nextDisplayedKey === displayedKey) return false;

      removeActivePopup();
      const collections = Object.fromEntries(
        Object.keys(HEX_OVERLAY_CATEGORIES).map((category) => [category, renderClusters(indexes[category], queryZoom)]),
      );
      setFeatureCollections(collections, queryZoom);
      displayedKey = nextDisplayedKey;
      return true;
    },
    getIndex(category) {
      return indexes?.[category] ?? null;
    },
    get timeKey() {
      return timeKey;
    },
  };
}

export function filteredOverlayReports(reportsByCategory, timeState) {
  assertReportsByCategory(reportsByCategory);
  return Object.freeze(Object.fromEntries(
    Object.keys(HEX_OVERLAY_CATEGORIES).map((category) => [
      category,
      timeState ? filterReportsByTime(reportsByCategory[category], timeState) : [...reportsByCategory[category]],
    ]),
  ));
}

export function timeStateKey(timeState) {
  if (!timeState) return "all";
  return `${timeState.startDate || "all"}:${timeState.selectedDate || "all"}`;
}

function assertReportsByCategory(reportsByCategory) {
  if (!reportsByCategory || typeof reportsByCategory !== "object") throw new TypeError("Expected overlay reports grouped by category.");
  Object.keys(HEX_OVERLAY_CATEGORIES).forEach((category) => {
    if (!Array.isArray(reportsByCategory[category])) throw new TypeError(`Expected an array of ${category} overlay reports.`);
  });
}
